// src/components/programs/ProgramForm/Step0Sections/SortableCitiesList.jsx

import React from 'react';
import { Reorder, AnimatePresence } from 'framer-motion';
import { Paper, Box, Typography, IconButton, alpha, useTheme } from '@mui/material';
import { DragIndicator, Close, LocationOn } from '@mui/icons-material';

/**
 * Sortable list of cities using framer-motion Reorder
 */
const SortableCitiesList = ({ 
    selectedIds = [], 
    citiesOptions = [], 
    onReorder, 
    onRemove, 
    isArabic 
}) => {
    const theme = useTheme();

    // Map IDs to full objects for display, preserving the current order in selectedIds
    const selectedCities = selectedIds
        .map(id => citiesOptions.find(opt => String(opt.id) === String(id)))
        .filter(Boolean);

    if (selectedCities.length === 0) return null;

    return (
        <Box sx={{ mt: 2.5 }}>
            <Typography 
                variant="subtitle2" 
                sx={{ 
                    mb: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '0.85rem'
                }}
            >
                <LocationOn sx={{ fontSize: 18 }} />
                {isArabic ? 'ترتيب مدن البرنامج (اسحب للترتيب):' : 'Itinerary City Order (Drag to reorder):'}
            </Typography>
            
            <Reorder.Group
                axis="y"
                values={selectedIds}
                onReorder={onReorder}
                style={{ listStyle: 'none', padding: 0, margin: 0 }}
            >
                <AnimatePresence mode="popLayout">
                    {selectedCities.map((city, index) => (
                        <Reorder.Item
                            key={city.id}
                            value={city.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            style={{ marginBottom: '10px' }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.divider, 0.8),
                                    borderRadius: 2,
                                    bgcolor: theme.palette.background.paper,
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing', boxShadow: theme.shadows[3] },
                                    transition: 'border-color 0.2s, background-color 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                                    }
                                }}
                            >
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: 'primary.main',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    mr: 2
                                }}>
                                    {index + 1}
                                </Box>

                                <DragIndicator sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} />
                                
                                <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: 'text.primary' }}>
                                    {city.name}
                                </Typography>

                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemove(city.id);
                                    }}
                                    sx={{ 
                                        color: 'text.disabled', 
                                        '&:hover': { 
                                            color: 'error.main',
                                            bgcolor: alpha(theme.palette.error.main, 0.05)
                                        } 
                                    }}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            </Paper>
                        </Reorder.Item>
                    ))}
                </AnimatePresence>
            </Reorder.Group>
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                {isArabic 
                    ? '* سيتم عرض هذا الترتيب في جدول المواعيد والمنتجات.' 
                    : '* This order will determine the itinerary and product sequence.'}
            </Typography>
        </Box>
    );
};

export default SortableCitiesList;
